<script setup lang="ts">
import { ref, type PropType, onMounted, watch, computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import {
  type FormField,
  type MatrixColumn,
  type MatrixTableRow,
  MatrixColumnType,
  MatrixMode
} from '@/models/form';
import {
  ButtonType,
  evaluateCondition,
  getInitialMatrixData,
  MatrixCell,
  matrixDataCalculations
} from '@/components';
import cloneDeep from 'lodash/cloneDeep';
import { useEntryStore } from '@/stores/entry';
import CustomButton from '@/components/common/CustomButton.vue';
import { shiftTimestampByDateShift } from '@/utils';
import { DateTime } from 'luxon';
import type { CaseModel, MatrixValue } from '@/models';

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  },
  selectedCase: {
    type: Object as PropType<CaseModel>
  }
});
const localField = ref<FormField>(props.field);
const entryStore = useEntryStore();
const dynamicConditionRowAdded = ref(false);

const tableColumns = ref<MatrixColumn[]>([]);
const dataList = ref<MatrixTableRow[]>(
  localField.value.matrixTableRowValues ?? []
);

onMounted(() => {
  tableColumns.value.push({
    id: 0,
    label: '',
    key: 'rowHeader',
    type: MatrixColumnType.TEXT,
    disable: true,
    frozen: true
  });
  for (const column of localField.value.options.columns!) {
    const newColumn: MatrixColumn = {
      id: column.id,
      label: column.label,
      key: `column${column.id}`,
      type: column.type,
      options: column.options,
      disable: column.disable,
      frozen: column.frozen,
      tooltip: column.tooltip
    };
    tableColumns.value.push(newColumn);
  }

  if (
    !JSON.stringify(localField.value.conditions).includes('Show') &&
    localField.value.default
  ) {
    localField.value.show = true;
  }

  if (dataList.value.length == 0 && localField.value.matrixTableRowValues) {
    dataList.value = localField.value.matrixTableRowValues;
  }

  // only assign a value to the field if it is visible
  if (localField.value.show && (!localField.value.hasExistingMatrixValue || !localField.value.matrixTableRowValues)) {
    localField.value.matrixValues = localField.value.default ?
      JSON.parse(localField.value.default as string) :
      [];

    localField.value.matrixTableRowValues = getInitialMatrixData(
      localField.value
    );
    dataList.value = localField.value.matrixTableRowValues;
  }

  if (dataList.value?.length > 0 && !localField.value.hasExistingMatrixValue) {
    const defaultValue = localField.value.default ?
      JSON.parse(localField.value.default) :
      undefined;
    if (defaultValue) {
      localField.value.options.columns?.forEach(column => {
        const rowDefault: MatrixValue = defaultValue[0];
        if (rowDefault) {
          const selectedColumn = rowDefault.values?.find(
            defaultCol => `${column.id}` === `${defaultCol.id}`
          );
          if (selectedColumn && selectedColumn.dateShifting) {
            column.options!.dateShifting = selectedColumn.dateShifting;
            column.conditions = selectedColumn.conditions;
          }
        }
      });

      dataList.value.forEach(rowItem => {
        const rowDefault: MatrixValue = defaultValue.find(
          (row: MatrixValue) => row.rowId === rowItem.rowId
        );
        if (rowDefault) {
          for (const colVal of rowItem.columnValues!) {
            const selectedColumn = rowDefault.values?.find(
              defaultCol => `${colVal.id}` === `${defaultCol.id}`
            );
            if (selectedColumn) {
              colVal.options!.dateShifting = selectedColumn.dateShifting;
              colVal.conditions = selectedColumn.conditions;
            }
          }
        }
      });
    }
  }
});

const updateRowData = () => {
  matrixDataCalculations(localField.value.options.columns!, dataList.value);
  localField.value.matrixTableRowValues = dataList.value;
  localField.value.hasExistingMatrixValue = true;
};

const handleDynamicRowAddition = (matrixRow: MatrixTableRow) => {
  let index = 0;
  for (const rowData of dataList.value) {
    if (rowData.rowId === matrixRow.rowId) {
      const dynamicRow: MatrixTableRow = {
        rowId: '',
        rowHeader: rowData.rowHeader,
        enableClear: true,

        columnValues: JSON.parse(
          JSON.stringify(localField.value.options.columns)
        )
      };

      let indexToAddRow = 0;
      if (!rowData.dynamicRows) {
        dynamicRow.rowId = `${rowData.rowId}-1`;
        indexToAddRow = index + 1;
        rowData.dynamicRows = [dynamicRow.rowId];
      } else {
        dynamicRow.rowId = `${rowData.rowId}-${rowData.dynamicRows.length + 1}`;
        indexToAddRow = index + rowData.dynamicRows.length + 1;
        rowData.dynamicRows.push(dynamicRow.rowId);
      }

      let updatedMatrixData = [
        ...dataList.value.slice(0, indexToAddRow),
        dynamicRow,
        ...dataList.value.slice(indexToAddRow)
      ];

      updatedMatrixData = matrixDataCalculations(
        localField.value.options.columns!,
        updatedMatrixData
      );
      dataList.value = cloneDeep(updatedMatrixData);
      break;
    }

    index++;
  }
  localField.value.matrixTableRowValues = dataList.value;
};

const handleDynamicRowDeletion = (matrixRow: MatrixTableRow) => {
  const matrixData = dataList.value;
  const dynamicRowIndex = matrixData.findIndex(
    row => row.rowId == matrixRow.rowId
  );
  const deletedRow = matrixData.splice(dynamicRowIndex, 1);
  if (deletedRow) {
    const deletedRowId = deletedRow[0].rowId;
    for (const data of matrixData) {
      if (data.dynamicRows) {
        const dynamicRowIdIndex = data.dynamicRows.findIndex(
          rowId => rowId == deletedRowId
        );
        data.dynamicRows.splice(dynamicRowIdIndex, 1);
        if (data.dynamicRows.length == 0) {
          delete data.dynamicRows;
        }
      }
    }

    const updatedMatrixData = matrixDataCalculations(
      localField.value.options.columns!,
      matrixData
    );
    dataList.value = cloneDeep(updatedMatrixData);
  }
  localField.value.matrixTableRowValues = dataList.value;
};

watch(
  () => localField.value.matrixDependencyResolved,
  () => {
    if (
      localField.value.options.rows!.length == 1 &&
      localField.value.options.rows![0].dynamicRowConfig?.conditions &&
      !dynamicConditionRowAdded.value &&
      !localField.value.hasExistingMatrixValue
    ) {
      const dynamicCondition =
        localField.value.options.rows![0].dynamicRowConfig?.conditions[0];
      const [effect, condition] = dynamicCondition;

      if (effect == 'DynamicRows') {
        const expressionValue = evaluateCondition(
          condition,
          entryStore.form!.items!
        );

        const clonedData: MatrixTableRow[] = [];
        if (
          expressionValue &&
          !isNaN(expressionValue) &&
          dataList.value.length <= Math.round(expressionValue)
        ) {
          const columnValues = localField.value.options.columns!.map(col => ({
            ...col,
            value: '',
            isSelectedForInput: false
          }));
          const parentRow: MatrixTableRow = {
            rowId: '1',
            rowHeader: '',
            columnValues,
            dynamicRowConfig: {
              conditions: dynamicCondition,
              copyRowData: false,
              copyRowHeader: false
            }
          };
          clonedData.push(parentRow);
          const dynamicRows = [];
          for (let i = 1; i < Math.round(expressionValue); i++) {
            clonedData.push({
              ...cloneDeep(parentRow),
              rowId: `1-${i}`,
              enableClear: true,
              dynamicRowConfig: undefined
            });
            dynamicRows.push(`1-${i}`);
          }

          clonedData[0].dynamicRows = dynamicRows;
        }

        for (let i = 0; i < clonedData.length; i++) {
          const row = clonedData[i];
          for (const columnValue of row.columnValues!) {
            if (columnValue.default) {
              const dCondition = columnValue.default.map(item =>
                item == 'index' ? i : item == 'index+1' ? i + 1 : item
              );
              const expressionVal = evaluateCondition(
                dCondition,
                entryStore.form!.items!
              );

              if (
                expressionVal !== undefined &&
                expressionVal !== null &&
                !isNaN(expressionVal)
              ) {
                if (columnValue.type == MatrixColumnType.DROPDOWN) {
                  columnValue.choiceValue = columnValue.options?.choices?.find(
                    choice => choice.value == expressionVal
                  );
                } else if (columnValue.type == MatrixColumnType.NUMBER) {
                  columnValue.numberValue = expressionVal;
                }
              }
            }
          }
        }

        dataList.value = clonedData;
      }
    }
  },
  { deep: true }
);

watch(
  () => localField.value.options,
  () => {
    // Trying to avoid keeping tableColumns in the store, so just using the watch here to detect translation changes
    for (const column of localField.value.options.columns!) {
      for (const tableColumn of tableColumns.value) {
        if (column.id === tableColumn.id) {
          tableColumn.label = column.label;
          break;
        }
      }
    }
  },
  { deep: true }
);

const shiftDates = (column: MatrixColumn, colIndex: number) => {
  if (props.selectedCase?.shiftKey) {
    let shiftKey = props.selectedCase.shiftKey;
    if (column.options?.showingShiftedDate) {
      shiftKey = -shiftKey;
    }
    for (const row of localField.value.matrixTableRowValues!) {
      const currentDate = row.columnValues![colIndex - 1].dateValue;
      if (!currentDate) {
        continue;
      }
      const shiftdDate = shiftTimestampByDateShift(
        DateTime.fromJSDate(currentDate).toSeconds(),
        shiftKey
      );
      if (!column.options?.showingShiftedDate) {
        row.columnValues![colIndex - 1].unShiftedDate =
          DateTime.fromSeconds(shiftdDate).toJSDate();
      } else {
        row.columnValues![colIndex - 1].unShiftedDate = undefined;
      }
    }
    column.options!.showingShiftedDate = !column.options!.showingShiftedDate;
  }
};

const onRadioChangeListener = (rowData: MatrixTableRow, index: number) => {
  let allRadio = false;
  for (const column of localField.value.options.columns!) {
    allRadio = column.type == MatrixColumnType.SINGLE_CHOICE;
  }
  if (allRadio && !localField.value.options.mode) {
    for (const data of dataList.value) {
      if (data.rowId == rowData.rowId) {
        data.columnValues?.forEach(value => (value.booleanValue = false));
        data.columnValues![index].booleanValue = true;
        break;
      }
    }
  } else if (allRadio && localField.value.options.mode == MatrixMode.RANK) {
    for (const data of dataList.value) {
      if (data.rowId == rowData.rowId) {
        data.columnValues?.forEach(value => (value.booleanValue = false));
        data.columnValues![index].booleanValue = true;
      } else {
        data.columnValues![index].booleanValue = false;
      }
    }
  }
};

const allRadio = computed(() =>
  localField.value.options.columns!.every(
    column =>
      column.type === MatrixColumnType.SINGLE_CHOICE ||
      column.type === MatrixColumnType.MULTIPLE_CHOICE
  )
);
</script>

<template>
  <div class="table-root">
    <DataTable :value="dataList" :key="dataList.length">
      <Column v-for="(col, colIndex) of tableColumns"
        :key="col.id"
        :field="col.key"
        :header="''"
        :frozen="col.frozen">
        <template #header>
          <div class="column-header">
            <span v-html="col.label.replace('\\n', '<br>')"></span>
            <FontAwesomeIcon :icon="[`fas`, `square-info`]"
              v-if="col.tooltip"
              class="tooltip-icon"
              v-tooltip="col.tooltip"/>
          </div>
          <div class="button"
            v-if="col.options?.dateShifting && !props.previewMode">
            <CustomButton :text="
                col.options?.showingShiftedDate
                  ? 'Show original'
                  : 'Show shifted'
              "
              :type="ButtonType.OUTLINED"
              @onClick="() => shiftDates(col, colIndex)"
              v-ripple/>
          </div>
        </template>
        <template #body="{ data }">
          <MatrixCell :rowData="data"
            :column="col"
            :index="colIndex"
            :field="localField"
            :previewMode="previewMode"
            :allRadio="allRadio"
            @update:rowData="updateRowData"
            @onAddDynamicRow="handleDynamicRowAddition"
            @onDeleteDynamicRow="handleDynamicRowDeletion"
            @onRadioChangeListener="onRadioChangeListener"/>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped lang="scss">
:deep(.p-datatable-table-container) {
  overflow: scroll;
}
:deep(.p-datatable-table) {
  width: unset;
}
:deep(.p-datatable-column-header-content) {
  justify-content: center;
  flex-direction: column;
}
.column-header {
  display: flex;
  align-items: center;
}
.tooltip-icon {
  margin-left: 0.5rem;
  cursor: pointer;
}
:deep(.p-datatable-frozen-column) {
  position: sticky;
  background-color: white;
}
:deep(.p-datatable-tbody > tr > .p-datatable-frozen-column) {
  z-index: 1;
}
:deep(.p-datatable-tbody > tr > td.p-datatable-frozen-column),
:deep(.p-datatable-thead > tr > th.p-datatable-frozen-column) {
  box-shadow: 1px 0 5px -2px rgba(0, 0, 0, 0.4);
}
</style>
